import { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { getErrorMessage } from '@/api/api-client'
import { useAddPackageToUnite } from '@/api/package-req/mutations/useAddPackageToUnite'
import { TAX_FREE_THRESHOLD } from '@/api/payments/paymentsService'
import { AppDialog } from '@/components/AppDialog'
import { Divider } from '@/components/Divider'
import UniteIcon from '@/components/icons/pkg-unite.svg?react'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { getBrandLogo } from '@/helpers/getBrandLogo'
import {
  getUniteRequestShortId,
  hasUniteFlammableMix,
  isUniteGroupTooHeavy,
  PendingUniteGroup,
  PKG_SHRINK_FEE,
  PKG_UNITE_FEE_PER_PKG,
  UnitePkgSummary,
} from '@/helpers/pkgUtils'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/RoutePath'

import { UniteSuccessDialog } from './UniteSuccessDialog'

interface AddToExistingUniteDialogProps {
  isOpen: boolean
  onClose: () => void
  pkg: UnitePkgSummary
  uniteGroups: PendingUniteGroup[]
  /** Client shows UniteSuccessDialog; office shows a simple success toast. */
  successMode?: 'client' | 'office'
}

const UniteGroupCard = ({ group, selected }: { group: PendingUniteGroup; selected: boolean }) => {
  const { t } = useTranslation()
  const shortId = getUniteRequestShortId(group.uniteRequestId)
  const countLabel = t('unite.addToExistingPackageCount', { count: group.packages.length })
  const splitLabel = group.isSplit ? t('unite.addToExistingSplitLabel') : null

  return (
    <label
      className={cn(
        'border-border-default flex w-full cursor-pointer items-center gap-6 rounded-2xl border bg-white p-4 text-start shadow-sm',
        selected && 'border-button-secondary-border-default shadow-[0px_0px_0px_2px_rgba(0,174,255,0.2)]'
      )}
    >
      <RadioGroupItem value={group.uniteRequestId} className='size-5' />
      <div className='flex min-w-0 flex-1 items-center justify-between gap-4'>
        <div className='flex flex-col gap-1'>
          <p className='text-text-primary text-base font-medium'>{t('unite.addToExistingRequestTitle', { id: shortId })}</p>
          <p className='text-text-secondary text-sm'>
            {countLabel}
            {splitLabel ? ` | ${splitLabel}` : ''}
          </p>
        </div>
        <div className='flex flex-wrap items-center justify-end gap-4'>
          {group.packages.map(member => (
            <div key={member.id} className='flex flex-col items-center gap-1'>
              <img src={getBrandLogo(member.brand)} alt={member.brand} className='size-10 object-contain' />
              <span className='text-text-primary text-sm font-medium'>{member.brand.toUpperCase()}</span>
              <span className='text-text-secondary text-xs'>{member.toTrackingNumber || member.fromTrackingNumber}</span>
            </div>
          ))}
        </div>
      </div>
    </label>
  )
}

export const AddToExistingUniteDialog: FC<AddToExistingUniteDialogProps> = ({
  isOpen,
  onClose,
  pkg,
  uniteGroups,
  successMode = 'client',
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showCustomToast, showErrorToast, showSuccessToast } = useToast()
  const { mutateAsync: addPackageToUnite, isPending } = useAddPackageToUnite()
  const [selectedRequestId, setSelectedRequestId] = useState<string>('')

  useEffect(() => {
    if (!isOpen) {
      setSelectedRequestId('')
      return
    }
    if (uniteGroups.length === 1) {
      setSelectedRequestId(uniteGroups[0].uniteRequestId)
    }
  }, [isOpen, uniteGroups])

  const selectedGroup = useMemo(
    () => uniteGroups.find(group => group.uniteRequestId === selectedRequestId),
    [uniteGroups, selectedRequestId]
  )

  const handleConfirm = async () => {
    if (!selectedGroup) return
    const packagesAfterAdd = [...selectedGroup.packages, pkg]
    if (isUniteGroupTooHeavy(packagesAfterAdd)) {
      showErrorToast(t('unite.cannotUnite'), t('unite.tooHeavy'))
      return
    }
    if (hasUniteFlammableMix(packagesAfterAdd)) {
      showErrorToast(t('unite.cannotUnite'), t('unite.noLiquids'))
      return
    }

    try {
      const updatedReq = await addPackageToUnite({
        packageReqId: selectedGroup.uniteRequestId,
        packageId: pkg.id,
      })
      const packageCount = updatedReq.unitePackageIds?.length ?? packagesAfterAdd.length
      const isFreeUnite = packagesAfterAdd.every(p => p.fromTrackingNumber === packagesAfterAdd[0].fromTrackingNumber)
      const uniteFee = isFreeUnite ? 0 : packageCount * PKG_UNITE_FEE_PER_PKG
      const shrinkFee = updatedReq.shouldShrink ? PKG_SHRINK_FEE : 0
      onClose()
      if (successMode === 'office') {
        showSuccessToast(t('unite.addToExistingOfficeSuccess'))
        return
      }
      showCustomToast(close => (
        <UniteSuccessDialog
          packageCount={packageCount}
          uniteFee={uniteFee}
          shrinkFee={shrinkFee}
          onClose={() => {
            close()
            navigate(RoutePath.ClientPkg)
          }}
        />
      ))
    } catch (error) {
      showErrorToast(t('unite.addToExistingError'), getErrorMessage(error))
    }
  }

  return (
    <AppDialog isOpen={isOpen} onOpenChange={open => !open && onClose()} className='!max-w-[650px]' alwaysDialog>
      <div className='flex flex-col items-center gap-6'>
        <div className='flex flex-col items-center gap-4'>
          <UniteIcon className='text-icon-primary size-6 shrink-0' />
          <h2 className='text-text-primary text-center text-lg font-medium'>{t('unite.addToExistingTitle')}</h2>
        </div>

        <div className='flex w-full flex-col items-center gap-6'>
          <p className='text-text-secondary text-center text-sm leading-5 whitespace-pre-line'>
            {t(uniteGroups.length === 1 ? 'unite.addToExistingDescSingle' : 'unite.addToExistingDesc')}
          </p>

          <RadioGroup value={selectedRequestId} onValueChange={setSelectedRequestId} className='w-full gap-6'>
            {uniteGroups.map(group => (
              <UniteGroupCard key={group.uniteRequestId} group={group} selected={selectedRequestId === group.uniteRequestId} />
            ))}
          </RadioGroup>

          <div className='bg-button-primary-selected-segment text-text-secondary rounded-full px-2 py-1.5'>
            <p className='text-center text-xs leading-4'>{t('unite.addToExistingTaxPill', { threshold: TAX_FREE_THRESHOLD })}</p>
          </div>
        </div>

        <Divider />

        <Button disabled={!selectedRequestId || isPending} onClick={handleConfirm} size='lg'>
          {t('unite.addToExistingContinue')}
        </Button>
      </div>
    </AppDialog>
  )
}
